/**
 * Redesigned login screen.
 *
 * Preserves the Datacrate terminal/monospace identity:
 *   - JetBrains Mono / monospace family
 *   - uppercase tracking-widest labels
 *   - hard 2px borders, no rounded corners
 *   - brand primary accent color
 *
 * Layout:
 *   - A SINGLE centered sign-in column (the right-hand product/value-prop pane
 *     was removed). `BrandLogo` (clean wordmark) → bordered form card, on one
 *     vertical `space-y` rhythm, centered in the viewport.
 *   - Single primary "Sign in" button, secondary "Continue with Google" below.
 *   - Forgot-password reveal swaps the submit button for an inline reset field.
 *
 * Motion is restrained + on-brand: the column fades/rises in once on mount with
 * a shared easing curve; the preview pane owns its own (reduced-motion aware)
 * animation. Nothing animates position on a loop in the chrome itself.
 *
 * Behavioral parity: same firebase/auth helpers, same error toasts,
 * same Google popup → redirect fallback. Tests in
 * `__tests__/LoginScreen.test.tsx` lock the contract.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { LogIn, Loader2, Mail, KeyRound, Shield, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

import { signInWithGoogle, signInWithEmail, resetPassword } from '../../firebase';
import { isMfaRequiredError, resolverFromError } from '../../lib/auth/twoFactor';
import { setPendingMfaResolver } from '../AuthGate';
import { Button } from '../ui/button';
import { BrandHeader } from './BrandHeader';
import { VERSION_LABEL } from '../../lib/appVersion';
import { applyResolvedTheme } from '../../lib/theme';
import { useMarketingTheme } from '../marketing/useMarketingTheme';
import { GatewayPowerLink } from '../marketing/legalEntity';

export function LoginScreen(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  // Signed-out UI is outside App's Toaster. Keep feedback visible here too.
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; message: string } | null>(null);
  const showError = useCallback((message: string) => {
    setFeedback({ kind: 'error', message });
    toast.error(message);
  }, []);
  const showSuccess = useCallback((message: string) => {
    setFeedback({ kind: 'success', message });
    toast.success(message);
  }, []);

  // Match the theme the user picked on the pre-login marketing surface (the
  // landing toggle persists `dc-marketing-theme`). Without this the login screen
  // always renders in the app's own boot theme, so a light-mode landing handed
  // off to a dark login. Applied (not persisted) so a returning user's stored
  // app preference is untouched for future sessions; the in-app toggle still
  // owns `da-theme`.
  const { resolvedTheme: marketingTheme } = useMarketingTheme();
  useEffect(() => {
    applyResolvedTheme(marketingTheme);
  }, [marketingTheme]);

  const handleEmailSignIn = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);
    if (!email.trim() || !password.trim()) {
      showError('Enter your email and password to sign in');
      return;
    }
    setIsLoading(true);
    try {
      await signInWithEmail(email.trim(), password);
    } catch (err: unknown) {
      // 2FA-required errors are not failures — they pivot to the
      // TwoFactorChallenge screen via the AuthGate MFA slot.
      if (isMfaRequiredError(err)) {
        const resolver = resolverFromError(err);
        setPendingMfaResolver(resolver);
        return;
      }
      const code = (err as { code?: string })?.code;
      const msg =
        code === 'auth/too-many-requests'
          ? 'Too many attempts — try again later'
          : code === 'auth/wrong-password'
              || code === 'auth/invalid-credential'
              || code === 'auth/user-not-found'
            ? 'Invalid email or password'
            : 'Sign in failed — please check your credentials';
      showError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [email, password, showError]);

  const handleReset = useCallback(async () => {
    setFeedback(null);
    if (!email.trim()) {
      showError('Enter your email address first');
      return;
    }
    setIsResetting(true);
    try {
      await resetPassword(email.trim());
      showSuccess('If an account exists for that email, a reset link is on its way');
      setShowReset(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined;
      // The normal reset endpoint already sanitizes provider failures. Preserve
      // its fixed user-action messages; unexpected text gets a bounded fallback.
      showError(message === 'Enter a valid email address' || message === 'Too many reset requests'
        ? message : 'Failed to send reset email');
    } finally {
      setIsResetting(false);
    }
  }, [email, showError, showSuccess]);

  const handleGoogleSignIn = useCallback(async () => {
    setFeedback(null);
    try {
      await signInWithGoogle();
    } catch {
      showError('Google sign-in failed — try email/password or reload and use redirect');
    }
  }, [showError]);

  return (
    <div
      className="min-h-screen lg:h-screen w-full bg-background font-mono overflow-y-auto lg:overflow-hidden"
      /* Match the marketing landing accent (the CTA teal) — scopes every
         *-primary utility on the sign-in screen to the landing's --dc-cyan. */
      style={{ ['--primary' as string]: '#0ea5c4', ['--ring' as string]: '#0ea5c4' }}
    >
      <style>{LOGIN_CSS}</style>
      <div className="min-h-screen lg:h-full flex flex-col items-center justify-center px-4 py-12 lg:py-10">
        {/* Single centered sign-in column — the product/value-prop pane was
            removed; the form now sits on its own, centered. */}
        <div className="flex flex-col justify-center min-h-0 lg:overflow-y-auto w-full max-w-md mx-auto space-y-9 login-fade-up">
          <BrandHeader />

          <section
            className="border-2 border-border bg-background p-8 space-y-7"
            aria-label="Sign in"
          >
            <div className="flex items-center justify-center gap-2 text-text-secondary">
              <Shield className="w-4 h-4" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-widest">
                Sign In
              </span>
            </div>

            {feedback && (
              <p role={feedback.kind === 'error' ? 'alert' : 'status'}
                className="text-sm text-text-primary border border-border p-3">
                {feedback.message}
              </p>
            )}

            <form onSubmit={handleEmailSignIn} className="space-y-5">
              <div className="space-y-2">
                <label
                  htmlFor="login-email"
                  className="text-[9px] font-bold uppercase tracking-widest text-text-muted"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted"
                    aria-hidden="true"
                  />
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@company.com"
                    autoComplete="email"
                    required
                    className="w-full h-11 pl-10 pr-3 text-sm border-2 border-border bg-background text-text-primary focus:outline-none focus:border-primary focus-visible:border-primary rounded-md"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="login-password"
                    className="text-[9px] font-bold uppercase tracking-widest text-text-muted"
                  >
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowReset(v => !v)}
                    className="text-[9px] text-text-muted hover:text-primary uppercase tracking-widest"
                  >
                    {showReset ? 'Cancel' : 'Forgot?'}
                  </button>
                </div>
                <div className="relative">
                  <KeyRound
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted"
                    aria-hidden="true"
                  />
                  <input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    required={!showReset}
                    className="w-full h-11 pl-10 pr-3 text-sm border-2 border-border bg-background text-text-primary focus:outline-none focus:border-primary focus-visible:border-primary rounded-md"
                  />
                </div>
              </div>

              {showReset ? (
                <div className="border border-border-subtle bg-background p-3 space-y-2">
                  <p className="text-[9px] text-text-secondary uppercase tracking-widest leading-relaxed">
                    We&apos;ll send a one-time reset link to the email above.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isResetting}
                    className="h-8 w-full rounded-md uppercase tracking-widest text-[9px] font-bold"
                    onClick={handleReset}
                  >
                    {isResetting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" aria-label="Sending" />
                    ) : (
                      <>
                        Send reset email <ArrowRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 rounded-md uppercase tracking-widest text-[10px] font-bold"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" aria-label="Signing in" />
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" aria-hidden="true" /> Sign in
                    </>
                  )}
                </Button>
              )}
            </form>

            <div className="space-y-3">
              <p className="text-[8px] text-text-muted uppercase tracking-widest text-center">
                Or
              </p>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full h-10 border-2 border-border bg-background text-text-primary hover:border-primary focus:outline-none focus:border-primary rounded-md uppercase tracking-widest text-[10px] font-bold inline-flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" aria-hidden="true" />
                Continue with Google
              </button>
            </div>

            <p className="text-[8px] text-text-muted uppercase tracking-widest text-center leading-relaxed">
              By signing in you agree to the Datacrate terms of service.
            </p>
          </section>

          <div className="login-platform-credit flex flex-col items-center gap-4" data-theme={marketingTheme}>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-text-muted">by</span>
              <GatewayPowerLink />
            </div>
            {/* Discreet release version — single source of truth (src/lib/appVersion). */}
            <p
              className="text-[8px] text-text-muted uppercase tracking-widest text-center"
              data-testid="login-app-version"
            >
              {VERSION_LABEL}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * One-time entrance for the two columns. A single shared easing curve
 * (`cubic-bezier(0.22, 1, 0.36, 1)` — a gentle "out-expo"-ish settle) keeps the
 * motion calm and on-brand; the product pane lags slightly so the eye lands on
 * the form first. Fully disabled under `prefers-reduced-motion`.
 */
const LOGIN_CSS = `
.login-platform-credit {
  --dc-gw-ink: #ffffff;
  --dc-gw-ink2: #d4d4d4;
  --dc-gw-teal: #14b8a6;
}
.login-platform-credit[data-theme="light"] {
  --dc-gw-ink: #0b1118;
  --dc-gw-ink2: #525252;
  --dc-gw-teal: #0d9488;
}
.login-platform-credit .dc-gw { display: inline-flex; text-decoration: none; }
.login-platform-credit .dc-gw-lockup { display: inline-flex; align-items: flex-start; gap: 9px; }
.login-platform-credit .dc-gw-mark { display: block; width: 20px; height: 20px; flex: none; }
.login-platform-credit .dc-gw-word { display: block; width: 127.05px; height: 11.628px; margin-top: 4.612px; flex: none; }
.login-platform-credit .dc-gw:hover { opacity: .8; }
.login-platform-credit .dc-gw:focus-visible { outline: 2px solid var(--primary); outline-offset: 4px; border-radius: 4px; }
@keyframes login-fade-up {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.login-fade-up {
  animation: login-fade-up 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.login-fade-up--delayed { animation-delay: 0.12s; }
@media (prefers-reduced-motion: reduce) {
  .login-fade-up { animation: none !important; }
}
`;
